// Workspace card primitive — head bar + body, with pointer-event drag on the
// drag handle. Position is controlled by the parent (so it can persist to
// localStorage and recompute mini-map). No drag library: ~50 lines of native
// pointer events. Touch works because pointer events normalize across input
// types; the drag is disabled in the parent on tablets to avoid conflicting
// with scroll.
import { useCallback, useRef, useState } from 'react';
import type { ReactNode, PointerEvent as ReactPointerEvent } from 'react';

export interface CardPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Props {
  id: string;
  position: CardPosition;
  onMove: (id: string, dx: number, dy: number) => void;
  draggable?: boolean;
  /** Distinguishes the question card (yellow tint) from regular cards. */
  kind?: 'question' | 'standard';
  title?: string;
  presPill?: string;
  sourceCount?: number;
  children: ReactNode;
}

export function Card({
  id, position, onMove, draggable = true, kind = 'standard',
  title, presPill, sourceCount, children,
}: Props) {
  const [drag, setDrag] = useState<{ startX: number; startY: number; dx: number; dy: number } | null>(null);
  const handleRef = useRef<HTMLSpanElement>(null);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLSpanElement>) => {
    if (!draggable) return;
    e.preventDefault();
    handleRef.current?.setPointerCapture(e.pointerId);
    setDrag({ startX: e.clientX, startY: e.clientY, dx: 0, dy: 0 });
  }, [draggable]);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLSpanElement>) => {
    setDrag((cur) => cur ? { ...cur, dx: e.clientX - cur.startX, dy: e.clientY - cur.startY } : cur);
  }, []);

  const onPointerUp = useCallback((e: ReactPointerEvent<HTMLSpanElement>) => {
    handleRef.current?.releasePointerCapture(e.pointerId);
    setDrag((cur) => {
      if (cur && (cur.dx !== 0 || cur.dy !== 0)) {
        onMove(id, cur.dx, cur.dy);
      }
      return null;
    });
  }, [id, onMove]);

  const transform = drag ? `translate(${drag.dx}px, ${drag.dy}px)` : undefined;

  return (
    <div
      className={`ws-card ${kind === 'question' ? 'ws-card--question' : ''} ${drag ? 'ws-card--dragging' : ''}`}
      style={{
        // Position + size are controlled — necessary for absolute layout.
        // These three are NOT design tokens; they're per-instance state that
        // lives outside CSS. Drag transform is transient and falls back to none.
        left: position.x,
        top: position.y,
        width: position.w,
        height: position.h,
        transform,
      }}
    >
      {(title || kind === 'question') && (
        <div className="ws-card__head">
          <div className="ws-card__title-row">
            {draggable && (
              <span
                ref={handleRef}
                className="ws-card__drag-handle"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                aria-label="Drag card"
                role="button"
              >
                ⋮⋮
              </span>
            )}
            <span className="ws-card__title">{title ?? 'Question'}</span>
            {presPill && <span className="ws-card__pres-pill">{presPill}</span>}
          </div>
          <div className="ws-card__actions">
            {sourceCount !== undefined && <span className="ws-card__src-count">📑 {sourceCount}</span>}
            <button className="ws-card__btn" aria-label="More actions" title="More actions">⋯</button>
          </div>
        </div>
      )}
      <div className="ws-card__body">{children}</div>
    </div>
  );
}
